import { useEffect, useRef } from 'react';

/**
 * Run `callback` on an interval, automatically pausing while the tab is
 * hidden and resuming + firing once on visibility regain.
 *
 *   usePolling(() => fetchThreads({ silent: true }), 30_000);
 *
 * @param {() => void | Promise<void>} callback
 * @param {number} intervalMs
 * @param {boolean} [enabled=true]
 */
export function usePolling(callback, intervalMs, enabled = true) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let id = null;
    const start = () => {
      if (id != null) return;
      id = setInterval(() => callbackRef.current?.(), intervalMs);
    };
    const stop = () => {
      if (id != null) {
        clearInterval(id);
        id = null;
      }
    };

    if (document.visibilityState === 'visible') start();

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        callbackRef.current?.();
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs, enabled]);
}
